import getConfig from 'next/config';
import slugid from 'slugid';

import { slugifyForURL } from './slugify';

const { publicRuntimeConfig } = getConfig();

const MAX_CHARS_IN_URL_FROM_NAME = 300;

function trimSlashes(x) {
  return x.replace(/^\/+|\/+$/gm, '');
}

export const queryString = (params) => {
  return Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');
};

export const encodeHumanReadableSlug = (id, name) => {
  if (!id) {
    return null;
  }
  const firstPart = slugifyForURL(name, MAX_CHARS_IN_URL_FROM_NAME);
  const secondPart = slugid.encode(id);

  return `${firstPart}__${secondPart}`;
};

export const encodeHumanReadableURL = (id, name, edit = true, prefix = '', kind = 'scribe') => {
  if (!id) {
    console.error(`Received no ID to encodeHumanReadableURL: (id=${id}, name=${name}, kind=${kind})`);
    return '#';
  }
  let pathName = '';

  switch (kind) {
    case 'knowledge_page':
      pathName = 'page';
      break;
    case 'profile':
      pathName = 'profile';
      break;
    case 'public_profile':
      pathName = 'by';
      break;
    default:
      pathName = edit ? 'edit' : 'shared';
  }

  return `${publicRuntimeConfig.cursiveRootUrl}${pathName}/${encodeHumanReadableSlug(id, name)}`;
};

export const decodeHumanReadableURLToID = (url) => {
  if (!url) {
    return url;
  }

  const trimmedUrl = trimSlashes(url);
  const slug = trimmedUrl.slice(-22);

  if (slug) {
    try {
      return slugid.decode(slug);
    } catch (error) {
      return null;
    }
  } else {
    return url;
  }
};

export const getExternalIDFromPID = (pid) => {
  if (!pid || pid.length < 22) {
    console.error('Pid must be at least 22 characters long for successful decoding');
    return pid;
  }

  const v4Regex = new RegExp(/^[0-9A-F]?[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i);

  if (pid.match(v4Regex)) {
    return pid;
  }
  const newPid = `2${decodeHumanReadableURLToID(pid)}`;

  return newPid;
};

export const scribeShareURL = (id, name) => {
  if (id && id.length === 37) {
    id = id.slice(1, id.length);
  }
  return `${encodeHumanReadableURL(id, name, false)}`;
};

/**
 *
 * @param {*} id
 * @param {*} name
 * @param {*} kind
 * @param {Object} options
 * @param {boolean} [options.openEditMode=false]
 * @param {string} [options.as]
 * @param {boolean} [options.showUpdates=false]
 * @param {boolean} [options.showSuccess=false]
 * @param {string} [options.referrer]
 * @param {boolean} [options.knowledgePageSidepanel=false]
 * @returns
 */
export const scribeShareURLv2 = (
  id,
  name,
  kind = 'scribe',
  options = { openEditMode: false, showUpdates: false, showSuccess: false, referrer: '', as: '', knowledgePageSidepanel: false }
) => {
  if (id && id.length === 37) {
    id = id.slice(1, id.length);
  }
  const { openEditMode, showUpdates, showSuccess, referrer, as, knowledgePageSidepanel } = options;
  const params = {};

  if (openEditMode) {
    params.editmode = 'true';
  }
  if (showUpdates) {
    params.showupdates = 'true';
  }
  if (showSuccess) {
    params.showSuccess = 'true';
  }

  if (knowledgePageSidepanel) {
    params.knowledgePageSidepanel = 'true';
  }

  if (referrer) {
    params.referrer = referrer;
  }
  if (as) {
    params.as = as;
  }
  return `${encodeHumanReadableURL(id, name, false, true, kind)}${Object.keys(params).length > 0 ? `?${queryString(params)}` : ''}`;
};

export const scribeEditURL = (id, name) => {
  if (id && id.length === 37) {
    id = id.slice(1, id.length);
  }
  return `${encodeHumanReadableURL(id, name, true)}`;
};

export const knowledgePageURL = (id, name) => {
  return `${publicRuntimeConfig.cursiveRootUrl}page/${encodeHumanReadableSlug(id, name)}`;
};

export const documentURL = (id, name, type) => {
  switch (type) {
    case 'scribe':
      return scribeShareURL(id, name);
    case 'knowledge_page':
      return knowledgePageURL(id, name);
    default:
      return '#';
  }
};

export const normalizeId = (id) => (id?.length === 37 && id[0] === '2' ? id.substring(1) : id);
